import { useEffect, useState } from 'react';
import { AlertTriangle, Download, RefreshCw, X } from 'lucide-react';
import { Button } from '../../_shared/components';
import { calculateRowReach, calculateWillSend, calculateSenderGaps, CHANNELS, type WaterfallRow, type SenderGap, evalCondition } from './waterfallTypes';
import type { Campaign, CampaignMember, EnrichmentFlag, StatusMapping } from './types';
import type { DispatchLogRow } from '../api-client';
import { useSaveAsTemplate } from '../hooks/useSaveAsTemplate';
import { useReenrichDispatch } from '../hooks/useReenrichDispatch';
import { useLaunchDispatch } from '../hooks/useLaunchDispatch';
import { isUnconfirmed } from '../hooks/unconfirmed-write';

interface LaunchSidebarProps {
  campaign: Campaign;
  waterfallRows: WaterfallRow[];
  members: CampaignMember[];
  enrichmentFlags?: EnrichmentFlag[];
  statusMapping?: StatusMapping;
  isLaunched?: boolean;
  dispatchLogRows?: DispatchLogRow[];
  onClose: () => void;
  onLaunched: () => void;
}

export function LaunchSidebar({ campaign, waterfallRows, members, enrichmentFlags = [], statusMapping, isLaunched = false, dispatchLogRows, onClose, onLaunched }: LaunchSidebarProps) {
  const [visible, setVisible] = useState(false);
  const saveTemplate = useSaveAsTemplate();
  const reenrichMutation = useReenrichDispatch();
  const launchDispatchMutation = useLaunchDispatch();
  const [flagReenrichState, setFlagReenrichState] = useState<Record<string, 'idle' | 'pending' | 'success' | 'error'>>({});

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  const draftIssues = waterfallRows.flatMap(row =>
    Object.entries(row.cells)
      .filter(([, cell]) => cell.enabled && cell.copy_status !== 'ready')
      .map(([col]) => ({ rowName: row.name || 'Unnamed', col }))
  );

  const _rowReach = calculateRowReach(members, waterfallRows, statusMapping);
  const willSend = calculateWillSend(members, waterfallRows, statusMapping ?? {});
  const totalWillSend = Object.values(willSend).reduce((a, b) => a + b, 0);

  const exclusionFlags = enrichmentFlags.filter(f => f.type === 'exclusion');

  const senderGaps = calculateSenderGaps(members, waterfallRows, statusMapping ?? {});

  const exclusionCounts = exclusionFlags
    .map(f => {
      const flagMembers = members.filter(m => evalCondition(m, f.condition));
      return { flag: f, count: flagMembers.length, memberIds: flagMembers.map(m => m.id), flagMembers };
    })
    .filter(x => x.count > 0);

  const handleFlagDownloadCsv = (item: typeof exclusionCounts[0]) => {
    const header = ['Name', 'Email', 'Title', 'Account', 'Sales Tier', 'CM Status'];
    const rows = item.flagMembers.map(m =>
      [m.contact_name ?? '', m.contact_email ?? '', m.contact_title ?? '', m.account_name ?? '', m.sales_tier ?? '', m.cm_status ?? '']
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign.name.replace(/[^a-z0-9]/gi, '_')}_${item.flag.label.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFlagReenrich = (item: typeof exclusionCounts[0]) => {
    setFlagReenrichState(s => ({ ...s, [item.flag.id]: 'pending' }));
    reenrichMutation.mutate(
      { campaignId: campaign.id, memberIds: item.memberIds },
      {
        onSuccess: () => setFlagReenrichState(s => ({ ...s, [item.flag.id]: 'success' })),
        onError: () => setFlagReenrichState(s => ({ ...s, [item.flag.id]: 'error' })),
      },
    );
  };

  const handleLaunch = (direct = false) => {
    launchDispatchMutation.mutate(
      { campaignId: campaign.id, waterfall_rows: waterfallRows, status_mapping: statusMapping ?? {}, direct },
      {
        onSuccess: (data) => {
          if (isUnconfirmed(data)) {
            // The launch may have gone out; the buttons stay locked (isSuccess) and the
            // summary tells the user to check the dispatch log.
            onLaunched();
            return;
          }
          if (waterfallRows.length > 0) {
            saveTemplate.mutate({
              name: campaign.name,
              campaign_type: campaign.type,
              rows: waterfallRows,
              source: 'launch',
              campaign_sfdc_id: campaign.id,
              campaign_name: campaign.name,
            });
          }
          onLaunched();
          // Don't auto-close — show dispatch summary so user can review skip counts
        },
      },
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-30 bg-black/20 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />

      {/* Sidebar */}
      <div className={`fixed inset-y-0 right-0 z-40 w-[460px] bg-white dark:bg-nightshade-950 border-l border-nightshade-200 dark:border-nightshade-800 shadow-xl flex flex-col transform transition-transform duration-200 ${visible ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          <div className="flex-1">
            <p className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50">{isLaunched ? 'Launch summary' : 'Launch campaign'}</p>
            <p className="text-xs text-nightshade-400 mt-0.5 truncate">{campaign.name}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Draft issues */}
          {draftIssues.length > 0 && (
            <div className="rounded-lg border border-pomegranate-200 dark:border-pomegranate-800 bg-pomegranate-50 dark:bg-pomegranate-950/30 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-pomegranate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-pomegranate-800 dark:text-pomegranate-300 mb-2">
                    {draftIssues.length} cell{draftIssues.length !== 1 ? 's' : ''} not ready
                  </p>
                  <ul className="space-y-1">
                    {draftIssues.map((issue, i) => (
                      <li key={i} className="text-xs text-pomegranate-700 dark:text-pomegranate-400">
                        <span className="font-medium">{issue.rowName}</span> — {issue.col}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Exclusion flags */}
          {exclusionCounts.length > 0 && (
            <div>
              <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-800 dark:text-nightshade-100 mb-2">Exclusion flags</p>
              <div className="rounded-lg border border-pomegranate-200 dark:border-pomegranate-800 bg-pomegranate-50 dark:bg-pomegranate-950/20 divide-y divide-pomegranate-100 dark:divide-pomegranate-900/40 overflow-hidden">
                {exclusionCounts.map((item) => {
                  const reenrichState = flagReenrichState[item.flag.id] ?? 'idle';
                  return (
                    <div key={item.flag.id} className="flex items-center gap-2 px-3 py-2">
                      <span className="text-xs font-medium text-pomegranate-800 dark:text-pomegranate-300 flex-1">{item.flag.label}</span>
                      <span className="text-xs tabular-nums text-pomegranate-700 dark:text-pomegranate-400 mr-1">
                        {item.count} {item.count === 1 ? 'member' : 'members'}
                      </span>
                      <button
                        title="Download CSV"
                        onClick={() => handleFlagDownloadCsv(item)}
                        className="p-1 rounded text-pomegranate-500 hover:text-pomegranate-700 hover:bg-pomegranate-100 dark:hover:bg-pomegranate-900/40 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        title={reenrichState === 'success' ? 'Sent to Clay' : 'Re-enrich'}
                        disabled={reenrichState === 'pending' || reenrichState === 'success'}
                        onClick={() => handleFlagReenrich(item)}
                        className="p-1 rounded text-pomegranate-500 hover:text-pomegranate-700 hover:bg-pomegranate-100 dark:hover:bg-pomegranate-900/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {reenrichState === 'success'
                          ? <span className="text-xs text-matcha-600">✓</span>
                          : reenrichState === 'error'
                          ? <span className="text-xs text-pomegranate-500">!</span>
                          : <RefreshCw className={`w-3.5 h-3.5 ${reenrichState === 'pending' ? 'animate-spin' : ''}`} />
                        }
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sender warnings */}
          {senderGaps.length > 0 && (
            <div>
              <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-800 dark:text-nightshade-100 mb-2">Sender warnings</p>
              <div className="rounded-lg border border-lemon-200 dark:border-lemon-800 bg-lemon-50 dark:bg-lemon-950/20 divide-y divide-lemon-100 dark:divide-lemon-900/40 overflow-hidden">
                {senderGaps.map(gap => (
                  <div key={`${gap.rowId}|${gap.column}|${gap.gapType}`} className="flex items-start gap-2 px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-lemon-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-lemon-800 dark:text-lemon-300">
                      <span className="font-medium">{gap.count} {gap.count === 1 ? 'member' : 'members'}</span>
                      {' '}in <span className="font-medium">{gap.rowName}</span> ({gap.column}) —{' '}
                      {gapMessage(gap)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Post-launch dispatch summary */}
          {launchDispatchMutation.isSuccess && launchDispatchMutation.data && isUnconfirmed(launchDispatchMutation.data) && (
            <div className="rounded-lg border border-nightshade-200 dark:border-nightshade-700 bg-nightshade-50 dark:bg-nightshade-800/50 p-4">
              <p className="text-sm font-semibold text-nightshade-800 dark:text-nightshade-200 mb-1">Launch submitted</p>
              <p className="text-xs text-nightshade-600 dark:text-nightshade-400">
                Summary unavailable. Check the dispatch log before launching again.
              </p>
            </div>
          )}
          {launchDispatchMutation.isSuccess && launchDispatchMutation.data && !isUnconfirmed(launchDispatchMutation.data) && (() => {
            const d = launchDispatchMutation.data;
            const skipReasons = d.skip_reasons;
            const parts: string[] = [];
            if (skipReasons?.cell_disabled) parts.push(`${skipReasons.cell_disabled} cell disabled`);
            if (skipReasons?.unmapped_status) parts.push(`${skipReasons.unmapped_status} unmapped status`);
            if (skipReasons?.no_row) parts.push(`${skipReasons.no_row} no row matched`);
            return (
              <div className="rounded-lg border border-matcha-200 dark:border-matcha-800 bg-matcha-50 dark:bg-matcha-900/20 p-4">
                <p className="text-sm font-semibold text-matcha-800 dark:text-matcha-300 mb-1">Launch queued</p>
                <p className="text-xs text-matcha-700 dark:text-matcha-400">
                  {d.queued} queued for dispatch · {d.skipped} skipped{parts.length > 0 ? ` (${parts.join(', ')})` : ''}
                </p>
              </div>
            );
          })()}

          {/* Member summary */}
          {isLaunched && dispatchLogRows && dispatchLogRows.length > 0 ? (
            /* Post-launch: show actual dispatch log counts as source of truth */
            (() => {
              const liveRows = dispatchLogRows.filter(r => !r.is_test);
              const sentCount = liveRows.filter(r => r.result === 'sent' || r.result === 'queued' || r.result === 'processing').length;
              const skippedCount = liveRows.filter(r => r.result === 'skipped').length;
              const failedCount = liveRows.filter(r => r.result === 'failed').length;
              const byBucket = new Map<string, number>();
              for (const r of liveRows.filter(r => r.result !== 'skipped' && r.result !== 'failed')) {
                const b = r.bucket ?? 'Unknown';
                byBucket.set(b, (byBucket.get(b) ?? 0) + 1);
              }
              return (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-800 dark:text-nightshade-100">Dispatch summary</p>
                    <span className="text-xs text-nightshade-500 dark:text-nightshade-400">{sentCount} dispatched · {skippedCount} skipped{failedCount > 0 ? ` · ${failedCount} failed` : ''}</span>
                  </div>
                  <div className="rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-nightshade-50 dark:bg-nightshade-800 border-b border-nightshade-200 dark:border-nightshade-700">
                          <th className="text-left px-3 py-2 font-mono font-medium text-nightshade-500 uppercase tracking-wider text-[11px]">Bucket</th>
                          <th className="text-right px-3 py-2 font-mono font-medium text-nightshade-500 uppercase tracking-wider text-[11px]">Dispatched</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(byBucket.entries()).map(([bucket, count], i) => (
                          <tr key={bucket} className={i < byBucket.size - 1 ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''}>
                            <td className="px-3 py-2 font-medium text-nightshade-800 dark:text-nightshade-200">{bucket}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-nightshade-600 dark:text-nightshade-400">{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()
          ) : waterfallRows.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-800 dark:text-nightshade-100">Member summary</p>
                <span className="text-xs text-nightshade-500 dark:text-nightshade-400">{totalWillSend} members will receive outreach</span>
              </div>
              <div className="rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-nightshade-50 dark:bg-nightshade-800 border-b border-nightshade-200 dark:border-nightshade-700">
                      <th className="text-left px-3 py-2 font-mono font-medium text-nightshade-500 uppercase tracking-wider text-[11px]">Bucket</th>
                      <th className="text-right px-3 py-2 font-mono font-medium text-nightshade-500 uppercase tracking-wider text-[11px]">Sends</th>
                      <th className="text-left px-3 py-2 font-mono font-medium text-nightshade-500 uppercase tracking-wider text-[11px]">Channels</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waterfallRows.map((row, i) => {
                      const sends = willSend[row.id] ?? 0;
                      const uniqueChannels = Array.from(new Set(
                        Object.values(row.cells)
                          .filter(c => c.enabled)
                          .map(c => CHANNELS.find(ch => ch.value === c.channel)?.label ?? c.channel)
                      ));
                      const isLast = i === waterfallRows.length - 1;
                      return (
                        <tr key={row.id} className={!isLast ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''}>
                          <td className="px-3 py-2 font-medium text-nightshade-800 dark:text-nightshade-200">
                            {row.name || <span className="italic text-nightshade-400">Unnamed</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-nightshade-600 dark:text-nightshade-400">{sends}</td>
                          <td className="px-3 py-2 text-nightshade-500 dark:text-nightshade-400">
                            {uniqueChannels.length > 0 ? uniqueChannels.join(', ') : <span className="italic">None</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-nightshade-100 dark:border-nightshade-800 flex-shrink-0 bg-nightshade-50 dark:bg-nightshade-800/50">
          <Button variant="ghost" size="sm" onClick={handleClose}>{isLaunched ? 'Close' : 'Cancel'}</Button>
          {!isLaunched && (
            <>
              <Button
                variant="ghost"
                size="sm"
                loading={launchDispatchMutation.isPending}
                disabled={draftIssues.length > 0 || launchDispatchMutation.isPending || launchDispatchMutation.isSuccess}
                onClick={() => handleLaunch(true)}
                title="Send directly via POST — bypasses pg-boss queue"
              >
                Launch (Direct)
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={launchDispatchMutation.isPending}
                disabled={draftIssues.length > 0 || launchDispatchMutation.isPending || launchDispatchMutation.isSuccess}
                onClick={() => handleLaunch(false)}
              >
                Launch
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function gapMessage(gap: SenderGap): string {
  if (gap.token === 'gs_owner_email') return 'no GS assigned — sender assigned downstream';
  if (gap.token === 'claydr_email') return 'no ClayDR assigned — sender assigned downstream';
  if (gap.gapType === 'integration_user') return 'GTME owner is a system account — sender assigned downstream';
  return 'no GTME email — sender assigned downstream';
}
